import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ExternalLink } from "lucide-react";
import Reveal from "../components/site/Reveal";
import Seo, { SITE_URL } from "../components/site/Seo";
import { BRAND, DIVISIONS } from "../lib/site-data";

const CONTACT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Ocean Oasis",
  url: `${SITE_URL}/contact`,
  telephone: "+252610424241",
  email: "info@oceanoasis.llc",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sundus Plaza, Bondhere District",
    addressLocality: "Mogadishu",
    addressCountry: "SO",
  },
  openingHours: "Mo-Su 07:00-22:00",
  hasMap:
    "https://www.google.com/maps/search/?api=1&query=Sundus+Plaza+Bondhere+District+Mogadishu+Somalia",
};

export default function Contact() {
  return (
    <main data-testid="page-contact" className="overflow-hidden">
      <Seo
        title="Contact Ocean Oasis | Sundus Plaza, Bondhere District, Mogadishu"
        description="Visit Ocean Oasis at Sundus Plaza, Bondhere District, Mogadishu, Somalia. Call +252 610 424 241 or email info@oceanoasis.llc — supermarket & coffee open every day 7 AM – 10 PM."
        path="/contact"
        jsonLd={CONTACT_JSONLD}
      />
      {/* HERO */}
      <section className="relative pt-40 pb-20 bg-[#faf6f0]">
        <div className="container-x">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-label text-[#f59e0b] mb-6"
          >
            Contact · Visit · Find
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-[#0a3d7a] leading-[0.92] max-w-5xl"
            style={{ fontSize: "clamp(2.8rem, 8vw, 7rem)" }}
          >
            One address. <em className="italic text-[#f59e0b]">All three oases.</em>
          </motion.h1>
        </div>
      </section>

      {/* CONTACT GRID */}
      <section className="pb-24 md:pb-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Left: details */}
            <div className="lg:col-span-5 space-y-8">
              <Reveal>
                <ContactRow
                  Icon={MapPin}
                  label="Address"
                  testid="contact-address"
                  primary={`${BRAND.address.line1}, ${BRAND.address.line2}`}
                  secondary={`${BRAND.address.city}, ${BRAND.address.country}`}
                />
              </Reveal>
              <Reveal delay={0.05}>
                <ContactRow
                  Icon={Phone}
                  label="Telephone"
                  testid="contact-phone"
                  primary={BRAND.contact.phone}
                  href={`tel:${BRAND.contact.phone.replace(/\s/g, "")}`}
                />
              </Reveal>
              <Reveal delay={0.1}>
                <ContactRow
                  Icon={Mail}
                  label="Email"
                  testid="contact-email"
                  primary={BRAND.contact.email}
                  href={`mailto:${BRAND.contact.email}`}
                />
              </Reveal>
              <Reveal delay={0.15}>
                <div data-testid="contact-hours" className="flex gap-5 items-start">
                  <div className="w-12 h-12 flex-shrink-0 bg-[#0a3d7a] text-[#faf6f0] flex items-center justify-center">
                    <Clock size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="font-label text-[#f59e0b] mb-1">Hours</p>
                    <div className="space-y-3 mt-2">
                      <div>
                        <p className="font-body font-semibold text-[#0a3d7a] text-base">
                          Supermarket &amp; Coffee
                        </p>
                        <p className="font-display text-xl md:text-2xl text-[#0a3d7a]/90 leading-tight">
                          Every day · 7:00 AM – 10:00 PM
                        </p>
                      </div>
                      <div>
                        <p className="font-body font-semibold text-[#0a3d7a] text-base">
                          Logistics Office
                        </p>
                        <p className="font-body text-sm text-[#0a3d7a]/75 leading-relaxed">
                          {BRAND.contact.hoursLogistics}
                          <br />
                          {BRAND.contact.hoursLogisticsFri}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="pt-6 border-t border-[rgba(10,61,122,0.15)]">
                  <p className="font-label text-[#f59e0b] mb-4">
                    Divisions at this address
                  </p>
                  <ul className="space-y-2 font-body text-base text-[#0a3d7a]/85">
                    {DIVISIONS.map((d) => (
                      <li key={d.slug} className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-[#f59e0b] rounded-full" />
                        {d.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            {/* Right: map */}
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="relative border border-[rgba(10,61,122,0.15)] overflow-hidden aspect-[4/3] lg:aspect-[5/6] bg-[#0a3d7a]">
                <iframe
                  title="Ocean Oasis on Map"
                  data-testid="contact-map"
                  src="https://www.google.com/maps?q=Bondhere+District,+Mogadishu,+Somalia&output=embed"
                  className="w-full h-full"
                  style={{ border: 0, filter: "grayscale(0.35) contrast(1.05)" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Sundus+Plaza+Bondhere+District+Mogadishu+Somalia"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-map-link"
                className="mt-4 inline-flex items-center gap-2 font-label text-[#0a3d7a] hover:text-[#f59e0b] transition-colors"
              >
                Open in Google Maps <ExternalLink size={14} />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24 md:py-32 bg-[#0a3d7a] text-[#faf6f0]">
        <div className="container-x">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <p className="font-label text-[#f59e0b] mb-5">A Note</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                  We're happy to help in person. Please call ahead for cargo,
                  customs and meeting-space enquiries — we'll have the right
                  team ready when you arrive.
                </h2>
              </div>
              <div className="lg:col-span-4 flex flex-wrap gap-4">
                <a
                  href={`tel:${BRAND.contact.phone.replace(/\s/g, "")}`}
                  data-testid="contact-cta-call"
                  className="btn-primary"
                  style={{ background: "#f59e0b", color: "#0a3d7a" }}
                >
                  Call Us
                </a>
                <a
                  href={`mailto:${BRAND.contact.email}`}
                  data-testid="contact-cta-email"
                  className="btn-outline"
                  style={{ borderColor: "#faf6f0", color: "#faf6f0" }}
                >
                  Email Us
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function ContactRow({ Icon, label, primary, secondary, href, testid }) {
  const PrimaryEl = href ? (
    <a
      href={href}
      data-testid={`${testid}-link`}
      className="hover:text-[#f59e0b] transition-colors"
    >
      {primary}
    </a>
  ) : (
    primary
  );
  return (
    <div data-testid={testid} className="flex gap-5 items-start">
      <div className="w-12 h-12 flex-shrink-0 bg-[#0a3d7a] text-[#faf6f0] flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div>
        <p className="font-label text-[#f59e0b] mb-1">{label}</p>
        <p className="font-display text-2xl md:text-3xl text-[#0a3d7a] leading-tight">
          {PrimaryEl}
        </p>
        {secondary && (
          <p className="font-body text-sm text-[#0a3d7a]/70 mt-1">
            {secondary}
          </p>
        )}
      </div>
    </div>
  );
}
